// API envelope utilities — every API response is `{ data: ..., meta?: ... }`
// or `{ error: { code, message, ... } }`.

export interface ApiError {
  code?: string;
  message?: string;
  details?: unknown;
}

export interface Envelope<T> {
  data?: T;
  meta?: Record<string, unknown>;
  error?: ApiError;
}

export class ApiCallError extends Error {
  status?: number;
  code?: string;
  details?: unknown;
  constructor(message: string, opts: { status?: number; code?: string; details?: unknown } = {}) {
    super(message);
    this.name = 'ApiCallError';
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
  }
}

/**
 * Unwraps an envelope returned from the SDK helpers (`{ data, error, response }` shape).
 * Throws ApiCallError on non-2xx responses or envelope-level errors.
 *
 * The SDK service helpers return `data: unknown` because the OpenAPI spec doesn't
 * declare strict response schemas yet, so we accept a loose input type and assert
 * the unwrapped shape via the generic.
 */
export function unwrap<T>(result: {
  data?: unknown;
  error?: unknown;
  response?: Response;
}): T {
  const status = result.response?.status;
  if (result.error || (status != null && status >= 400)) {
    const err = result.error as ApiError | undefined;
    const env = (result.data as Envelope<T> | undefined) ?? undefined;
    const message =
      (env?.error?.message as string | undefined) ??
      (err?.message as string | undefined) ??
      `API 오류 (HTTP ${status ?? '?'})`;
    throw new ApiCallError(message, {
      status,
      code: env?.error?.code ?? err?.code,
      details: env?.error?.details ?? err?.details,
    });
  }
  const env = result.data as Envelope<T> | T;
  if (env && typeof env === 'object' && 'data' in (env as Record<string, unknown>)) {
    const inner = (env as Envelope<T>);
    if (inner.error) {
      throw new ApiCallError(inner.error.message ?? '알 수 없는 오류', {
        status,
        code: inner.error.code,
        details: inner.error.details,
      });
    }
    return inner.data as T;
  }
  return env as T;
}

/**
 * Unwraps a paginated envelope shape `{ data: { items: T[], count, limit, cursor, has_more }, meta }`
 * and returns the inner `items` array. Returns `[]` if the response is missing/malformed
 * so callers can safely spread/iterate without runtime errors.
 *
 * Used for list endpoints (analytics daily/hourly/platforms/stores/menus, /v1/stores, ...).
 * For singleton-object responses (e.g. `/me`, analytics `topline`) keep using `unwrap`.
 */
export function unwrapItems<T>(result: {
  data?: unknown;
  error?: unknown;
  response?: Response;
}): T[] {
  const inner = unwrap<unknown>(result);
  if (!inner || typeof inner !== 'object') return [];
  const items = (inner as { items?: unknown }).items;
  return Array.isArray(items) ? (items as T[]) : [];
}
